import ViewLocalStateProcessor from "./viewStateProcessors/ViewLocalStateProcessor.js";
import ViewViewStateProcessor from "./viewStateProcessors/ViewViewStateProcessor.js";
import ViewLocalStateStorage from "./viewStateStorages/ViewLocalStateStorage.js";
import ViewViewStateStorage from "./viewStateStorages/ViewViewStateStorage.js";
import ViewSharedStateProcessor from "./viewStateProcessors/ViewSharedStateProcessor.js";
import ViewSharedStateStorage from "./viewStateStorages/ViewSharedStateStorage.js";
import {FunctionHelper, MessageDialog, ObjectHelper, StringHelper} from "@bryntum/schedulerpro";
import {
  AdminViewRoutes,
  DEFAULT_VIEW_STATE,
  VIEW_DEF_PREF,
  VIEW_STATE_PIITS_UPDATE_LISTENER,
  VIEW_TYPES
} from "../../constants/OtherKeys.jsx";
import {generateMultiFilterConfigFromModel, getURLWithBoardSlug } from "../../utils/utils.jsx";
import {PERSISTENCE_STATE_KEYS} from "../../constants/PersistenceStateKeys.jsx";
import {View} from "../../../bryntum-lib/models/View.jsx";
import ViewSharedUtil from "./ViewSharedUtil.js";
import { userPreferences } from "../../../react/context/bryntumContext.jsx";

const viewStateProcessorKey = "viewState";

class ViewManager {

  constructor(viewsStore, viewsFolderStore, userPreferences, user, board, history) {
    this.bufferedViewStateChange = FunctionHelper.createBuffered(this.#onViewStateChangePersist.bind(this), 100);

    this.viewsStore = viewsStore;
    this.viewsFolderStore = viewsFolderStore;
    this.user = user;
    this.userPreferences = userPreferences;
    this.selectedBoard = board;
    this.routerHistory = history;

    this.viewStateListener = [];
    this.viewsStoreListener = null;

    this.viewStateProcessors = [ // the order matters here
      new ViewLocalStateProcessor("localState"),
      new ViewSharedStateProcessor("viewSharedState"),
      new ViewViewStateProcessor(viewStateProcessorKey, "viewSharedState")
    ];

    this.viewStateStorages = [ // the order matters here
      new ViewLocalStateStorage("localState"),
      new ViewSharedStateStorage("viewSharedState"),
      new ViewViewStateStorage(viewStateProcessorKey)
    ];

    this.renderState = {
      selectedView: null,
    }
    this.pageViewsRef = null;

    // this is added to notify function on the app header for particularly ticksize text update
    this.appHeaderNotifyOnStataChange = null;
  }

  init() {
    this.#migrateDefaultViews();
    this.#selectLastSelectedView(DEFAULT_VIEW_STATE);

    // synchronizing page views with browser navigation
    this.windowPopstateListener = this.#handleBrowserBackForthStateChange.bind(this);
    window.addEventListener("popstate", this.windowPopstateListener);
    // adding listener so if a view is deleted either from socket or sync both will be handle from single place
    this.viewsStoreListener = this.viewsStore.on("beforeRemove", ({ records: viewsToDelete }) => {
      const selectedViewToDelete = viewsToDelete.find((view) => view.id === this.renderState.selectedView.id);
      if (selectedViewToDelete) {
        this.selectDefaultViewOfCurrentView();
      }
    })
  }

  getViews() {
    return this.viewsStore;
  }

  selectView(viewToSelect, { canNavigate = true, replaceRouteInstead = false, forceLoadView = false } = {}, routeState = {}) {
    // we could have a getter in modal of view (when available) to clone from there
    const currentViewProxy = new window.Proxy(viewToSelect, {
      get(target, prop) {
        if (prop === "viewState") {
          return ObjectHelper.clone(target[prop]);
        }

        if (prop === "hasPIITS") {
          const viewStateInPIITS =  StringHelper.safeJsonParse(userPreferences.getSetting(PERSISTENCE_STATE_KEYS.viewStatePIITS, "{}"));
          return (viewStateInPIITS && Object.keys(viewStateInPIITS).length) ? viewStateInPIITS : null;
        }

        return target[prop];
      }
    });
    const viewToBeSelected = currentViewProxy;
    const selectedView = this.renderState.selectedView;
    if ((selectedView?.id !== viewToBeSelected.id || forceLoadView) && canNavigate) {
      this.#navigateToViewRoute(viewToBeSelected, replaceRouteInstead, routeState);
    }
    /* Clear view state in piits when user switches view, Checking for selected view since on first load this function is also invoked then we don't want to update view state in user preference */
    if (selectedView) this.clearAllPIITSChanges();
    this.#persistViewAsLastSelected(viewToBeSelected);
    this.#updateRenderState({ selectedView: viewToBeSelected });
  }

  // this is for routes which are not associated with the board views store such boardroom
  selectViewAnonymousView(id, viewName, viewType) {
    this.#updateRenderState({ selectedView: { id, name: viewName, viewType } });
  }

  createView(viewToCreate, selectOnCreate = true) {
    if (viewToCreate.name) {
      const existingView = this.viewsStore.find((view) => view.name?.toLowerCase() === viewToCreate.name?.toLowerCase());
      if (existingView) {
        return false;
      } else {
        viewToCreate.viewState = this.#getProcessedViewState();
        const [viewToSelected] = this.viewsStore.insert(0, viewToCreate);
        if (selectOnCreate) {
          this.selectView(viewToSelected);
        }
        return true;
      }
    }
  }

  updateView(viewToUpdateId, viewToUpdate, selectOnCreate = true) {
    if (viewToUpdate.name) {
      const existingView = this.viewsStore.find((view) => view.id === viewToUpdateId);
      if (existingView) {
        const viewModelKeys = Object.keys(generateMultiFilterConfigFromModel(View));
        const toUpdateObj = viewModelKeys.reduce((toUpdateObj, key) => {
          if (key !== "id" && viewToUpdate[key] && viewToUpdate[key] !== existingView[key]) toUpdateObj[key] = viewToUpdate[key];
          return toUpdateObj;
        }, {});
        if (this.renderState.selectedView.isDefaultView) {
          toUpdateObj.viewState = {
            ...toUpdateObj.viewState,
            ...existingView.viewState,
            customViewState: this.#getProcessedViewState(),
          }
        } else {
          toUpdateObj.viewState = this.#getProcessedViewState();
        }
        existingView.set(toUpdateObj);
        if (selectOnCreate) {
          this.selectView(existingView);
        }
        this.clearAllPIITSChanges();
        return true;
      }
    }
    return false;
  }

  deleteView(viewToDelete) {
    viewToDelete = this.getViewById(viewToDelete.id);
    if (viewToDelete) {
      this.viewsStore.remove(viewToDelete);
    } else {
      MessageDialog.alert({ title: "View Not Exist", message: "The view to delete doesn't exists!" });
    }
  }

  getViewById(viewId) {
    return this.viewsStore.getById(viewId);
  }

  getDefaultViewByType(viewType) {
    return this.viewsStore.find((view) => view.viewType === viewType && view.isDefaultView === true);
  }

  selectDefaultViewOfCurrentView() {
    const selectedView = this.renderState.selectedView;
    if (!selectedView.isDefaultView) {
      const defaultViewOfCurrentView = this.getDefaultViewByType(selectedView.viewType);
      if (defaultViewOfCurrentView) {
        this.selectView(defaultViewOfCurrentView);
      } else {
        MessageDialog.alert({ title: "Base View Not Exist", message: `The base view of "${selectedView.viewType}" doesn't exist!` });
      }
    }
  }

  getViewState({ viewType, initialDefaultState }) {
    return this.viewStateProcessors.reduce((prevProcessedState, viewStateProcessor) => {
      return viewStateProcessor.processStateRetrieval(prevProcessedState, this, viewType, initialDefaultState)
    }, {});
  }

  onViewStateChange({ oldState, viewType, state, initialDefaultState, storeModelClass }) {
    const cleanedState = ViewSharedUtil.cleanViewState(state, storeModelClass)
    const previousViewType = this.renderState.selectedView.viewType;
    this.bufferedViewStateChange({ oldState, viewType, state: cleanedState, initialDefaultState, storeModelClass, previousViewType });
  }

  // this is a special case where we need to copy state from custom view to the default view of it when user attempts to make changes in custom view
  // This function is also being called from `applySorterOnTimeAxisHeaderClick` in schedulerUtils file due to a hidden sort being applied on resource store.
  selectDefaultViewAndMapState(state) {
    // this is used by app header eye for blinking purpose. do not use it in else where in this file
    this.uiShouldEyeIconOnHeaderBlink = true;
    this.selectDefaultViewOfCurrentView();
    if (typeof state === "object") {
      Object.keys(state).forEach((viewTypeKey) => {
        if (this.pageViewsRef[viewTypeKey]) {
          this.pageViewsRef[viewTypeKey].state = { ...this.renderState.selectedView.viewState[viewTypeKey], ...state[viewTypeKey] };
        }
      });
    }
  }

  // consider methods with # prefix private and should not be used from outside of this file
  #getProcessedViewState(state) {
    const stateToSave = Object.keys(this.pageViewsRef).reduce((accum, viewKey) => {
      const widgetInstance = this.pageViewsRef[viewKey];
      // clean state before processing not after as this will cause the timeaxis view sorter to be saved in the viewstate (if on schedule view)
      const cleanedState = ViewSharedUtil.cleanViewState(state || widgetInstance.state, widgetInstance.store.swivuModelClassForMapping || widgetInstance.store.modelClass);
      const processedState = this.viewStateProcessors.reduce((prevProcessedState, viewStateProcessor) => {
        return viewStateProcessor.processStateMutation(prevProcessedState, cleanedState, widgetInstance.defaultState, this);
      }, {});
      accum[viewKey] = processedState.viewState;
      return accum;
    }, {});
    return ObjectHelper.clone(stateToSave);
  }

  // if a default view is selected, then we don't have any info in the selected record for about the view state this function extracts that info
  getCurrentDefaultViewStates() {
    return this.#getProcessedViewState();
  }

  #onViewStateChangePersist({ oldState, viewType, state, initialDefaultState, previousViewType }) {
    /*
      Since we have throttle when viewstate updates (in bufferedViewStateChange function), If user immediately switched from one view to another the states could get updated in wrong view.
      The condition is necessary to prevent issues when switching views:
        State Change Trigger: Switching to the scheduler view triggers a state change due to time axis grid scrolling.
        Unintended Updates: Switching directly from the scheduler to the resource view can cause unintended updates due to the state change as both have a resource key in view state object.
        Boardroom View Crash: Switching from the scheduler to the boardroom view can cause a console crash, as the boardroom view doesn't have a viewstate.
        Reproduction Steps: Remove the condition and switch between the scheduler and boardroom views immediately to reproduce the issue.
    */
    if (previousViewType !== this.renderState.selectedView.viewType) return;
    console.group("view:machine");
    const processedState = this.viewStateProcessors.reduce((prevProcessedState, viewStateProcessor) => {
      return viewStateProcessor.processStateMutation(prevProcessedState, state, initialDefaultState);
    }, {});

    console.log({ processedState });

    const selectedView = this.renderState.selectedView;
    if (selectedView.isDefaultView && !selectedView.viewState.customViewState?.[viewType]) {
      // during comparison within the isStoreStateChanged function we need a previous state from which we can compare the new processed state object
      selectedView.viewState = {
        ...selectedView.viewState,
        customViewState: {
          ...(selectedView.viewState.customViewState || {}),
          [viewType]: this.#getProcessedViewState(oldState)[viewType],
        }
      };
    }

    this.viewStateStorages.forEach((viewStateStorage) => {
      viewStateStorage.saveStateMutation(processedState, this, viewType);
    });

    console.groupEnd();

    if (this.appHeaderNotifyOnStataChange) {
      this.appHeaderNotifyOnStataChange(processedState);
    }
  }

  #persistViewAsLastSelected(viewToPersist) {
    this.userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.viewSelectedId, viewToPersist.id);
  }

  #selectLastSelectedView(orDefaultByType, forceLoadView = false) {
    const lastSelectedViewId = StringHelper.safeJsonParse(this.userPreferences.getSetting(PERSISTENCE_STATE_KEYS.viewSelectedId));
    let viewToSelect = lastSelectedViewId && this.getViewById(lastSelectedViewId);
    if (!viewToSelect) {
      viewToSelect = this.getDefaultViewByType(orDefaultByType);
    }
    if (viewToSelect) {
      this.selectView(viewToSelect, { replaceRouteInstead: true, forceLoadView });
    } else {
      MessageDialog.alert({title: "No base View To Select", message: "Unable to find any base view to select!" });
    }
  }

  #migrateDefaultViews() {
    this.#cleaningUpExistingViews();
    this.#populateDefaultViews();
  }

  #populateDefaultViews() {
    const defaultViews = Object.entries(VIEW_DEF_PREF);
    // for now, we have only a single default folder that is why we are just assigning the first one
    const viewsToAddInStore = defaultViews.reduce((viewsToAdd, [defaultViewType, defaultView]) => {
      const existingView = this.getDefaultViewByType(defaultViewType);
      if (existingView) {
        if (existingView.isOwnView) {
          existingView.name = defaultView.name;
        }
        // You can update any value that you want to always come from config, but that will always overwrite user defined values
      } else {
        const viewRecord = {
          viewType: defaultViewType,
          isDefaultView: true,
          folderId: this.viewsFolderStore.find((folder) => folder.type === defaultView.folderType)?.id,
          name: defaultView.name,
          viewDescription: defaultView.viewDescription,
          iconId: defaultView.iconId,
          color: defaultView.color,
          viewState: {},
          isShareToHQ: false,
          isShareToWorkforce: false,
          isShareToPublic: false,
          isShareToClient: false,
        };
        const viewToAdd = this.viewsStore.createRecord(viewRecord);
        viewsToAdd.push(viewToAdd);
      }
      return viewsToAdd;
    }, []);
    if (viewsToAddInStore.length) {
      this.viewsStore.add(viewsToAddInStore);
    }
  }

  #cleaningUpExistingViews() {
    // cleaning up existing views that doesn't have default view exists in VIEW_DEF_PREF
    const defaultViewsType = Object.keys(VIEW_DEF_PREF);
    const viewsToRemove = this.viewsStore.query((view) => !defaultViewsType.includes(view.viewType));
    if (viewsToRemove.length) {
      this.viewsStore.remove(viewsToRemove);
    }
  }

  #navigateToViewRoute(viewToNavigateTo, replaceRouteInstead = false, otherState = undefined) {
    const updateURL = getURLWithBoardSlug(AdminViewRoutes[viewToNavigateTo.viewType], this.selectedBoard.slug);
    // don't use push function with object param overload as it behaves wrong
    if (replaceRouteInstead) {
      this.routerHistory.replace(updateURL, {viewId: viewToNavigateTo.id, ...otherState});
    } else {
      this.routerHistory.push(updateURL, {viewId: viewToNavigateTo.id, ...otherState});
    }
  }

  #updateRenderState(newState) {
    this.renderState = { ...this.renderState, ...newState };
    this.notifyStateListeners();
  }

  #handleBrowserBackForthStateChange({ state }) {
    const { viewId: lastViewId, viewType = "" } = state?.state || {};
    if (lastViewId) {
      const selectedViewId = this.renderState.selectedView.id;
      if (selectedViewId !== lastViewId) {
        if (viewType === VIEW_TYPES.mySwivu) {
          this.selectViewAnonymousView(lastViewId, "My Swivu", viewType);
        } else {
          const viewToSelect = this.getViewById(lastViewId);
          if (viewToSelect) {
            this.selectView(viewToSelect, { canNavigate: false });
          } else {
            // If view to select is not found then selecting last selected view or default view of the type.
            // For example: If `lastViewId` does not exist on current board but on previously selected board.
            this.#selectLastSelectedView(DEFAULT_VIEW_STATE, true)
          }
        }
      }
    }
  }

  cleanUp() {
    window.removeEventListener("popstate", this.windowPopstateListener);
    this.viewsStoreListener();
  }

  setPIITSChanges(changes = {}) {
    this.userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.viewStatePIITS, JSON.stringify(changes))
    this.userPreferences.trigger(VIEW_STATE_PIITS_UPDATE_LISTENER);
  }

  clearAllPIITSChanges() {
    this.userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.viewStatePIITS, "{}");
    this.userPreferences.trigger(VIEW_STATE_PIITS_UPDATE_LISTENER);
  }

  clearPIITSChangesByViewType(viewType) {
    const prevPIITS = this.renderState.selectedView.hasPIITS;
    /* Clearing piits by view type if prev piits exist */
    if (prevPIITS && prevPIITS[viewType]) {
      delete prevPIITS[viewType];
      this.userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.viewStatePIITS, JSON.stringify(prevPIITS));
      this.userPreferences.trigger(VIEW_STATE_PIITS_UPDATE_LISTENER);
    }
  }

  goToPreviousView() {
    this.#selectLastSelectedView(DEFAULT_VIEW_STATE);
  }

  // REACT SPECIFIC FUNCTIONS
  notifyStateListeners() {
    this.viewStateListener.forEach((listener) => {
      listener();
    })
  }

  renderSubscribe(listenerCb) {
    this.viewStateListener = [...this.viewStateListener, listenerCb];
    return () => {
      this.viewStateListener = this.viewStateListener.filter((l) => l !== listenerCb);
    }
  }

  renderSnapshot() {
    return this.renderState;
  }
}

export default ViewManager;
